import React, { FC, Fragment, useState } from 'react';
import { MutationFunction } from 'react-apollo';
import { useTranslation } from 'react-i18next';
import { Grid, Typography } from '@material-ui/core';
import { map, pull, remove } from 'lodash';
import { connect, FormikProps } from 'formik';

import {
  ClientLocation,
  UpdateUserSegmentComponent,
  UpdateUserSegmentInput,
  UserSegment,
} from 'api';
import { AddLocation, MoreLessToggle, RemoveLocation } from 'components';
import { useNotify } from 'hooks';

import { LOCATION_ATTRIBUTES, VISIBLE_ITEMS } from './constants';

interface IProps {
  segment: UserSegment;
  locations: ClientLocation[];
  onSave: () => void;
}

interface IPropsEnhanced extends IProps {
  formik: FormikProps<UpdateUserSegmentInput>;
}

const LocationsEdit: FC<IPropsEnhanced> = ({ segment, locations, formik, onSave }) => {
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation();
  const notify = useNotify();

  function getIds() {
    const { ids } = LOCATION_ATTRIBUTES;
    return segment[ids];
  }

  function getLocations(): ClientLocation[] {
    const { items } = LOCATION_ATTRIBUTES;
    return segment[items];
  }

  function handleAdd(location: ClientLocation, mutation: MutationFunction) {
    const { ids } = LOCATION_ATTRIBUTES;
    const modifiedLocations = [location].concat(getLocations());
    const modifiedLocationIds = map(modifiedLocations, 'id');

    return mutation({
      variables: {
        data: {
          ...formik.values,
          [ids]: modifiedLocationIds,
        },
      },
    });
  }

  function handleRemove(location: ClientLocation, mutation: MutationFunction) {
    const { items, ids } = LOCATION_ATTRIBUTES;
    const modifiedLocations = getLocations();
    const previousLocationIds = map(modifiedLocations, 'id');
    const modifiedLocationIds = pull(previousLocationIds, location.id);

    remove(modifiedLocations, locationToRemove => locationToRemove.id === location.id);

    return mutation({
      variables: {
        data: {
          ...formik.values,
          [ids]: modifiedLocationIds,
        },
      },
      optimisticResponse: {
        updateUserSegment: {
          ...segment,
          [items]: modifiedLocations,
        },
      },
    });
  }

  function renderLocations(mutation: MutationFunction) {
    const length = expanded ? undefined : VISIBLE_ITEMS;
    const showToggle = getLocations().length > VISIBLE_ITEMS;
    const remaining = getLocations().length - VISIBLE_ITEMS;
    const sliced = getLocations().slice(0, length);

    return (
      <Fragment>
        {sliced.map(location => (
          <RemoveLocation
            canRemove
            key={location.id}
            location={location}
            onRemove={locationToRemove => handleRemove(locationToRemove, mutation)}
            removeKey="admin:onboardingRemoveLocation"
            confirmKey="common:delete"
          />
        ))}
        {showToggle && (
          <MoreLessToggle
            remaining={remaining}
            expanded={expanded}
            onClose={() => setExpanded(false)}
            onOpen={() => setExpanded(true)}
          />
        )}
      </Fragment>
    );
  }

  return (
    <Grid container>
      <Grid item xs={3}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={2} />
          <Grid item>
            <Typography variant="subtitle2">{t('common:location_plural')}</Typography>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={9}>
        <UpdateUserSegmentComponent onError={notify.mutationError} onCompleted={onSave}>
          {mutation => (
            <Fragment>
              <AddLocation
                locations={locations}
                exclusions={getIds()}
                onChange={location => handleAdd(location, mutation)}
                TextFieldProps={{ label: null, fullWidth: true }}
              />
              {renderLocations(mutation)}
            </Fragment>
          )}
        </UpdateUserSegmentComponent>
      </Grid>
    </Grid>
  );
};

export default connect<IProps>(LocationsEdit);
